import styled from "@emotion/styled";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useState } from "react";

import IconLink from "~/components/links/IconLink";
import BaseLink from "~/components/links/BaseLink";
import IconButton from "~/components/buttons/IconButton";
import Button from "~/components/buttons/Button";
import ButtonLink from "~/components/links/ButtonLink";
import { Icon } from "~/components/links/ActionButton";
import GatedDocumentButton from "~/components/buttons/GatedDocumentButton";

import Form from "~/queryBlocks/Form";

import { h3 } from "~/styles/typographyStyles";
import transition from "~/utilities/transition";
import FaIcon from "./FaIcon";

import trackCustomEvent from "~/utilities/trackCustomEvent";

const ctaWrapperCss = ({ backgroundColor, theme }) => css`
    padding: 30px 35px;
    width: 100%;
    background-color: ${backgroundColor === "white"
        ? theme.colors.backgroundColor.defaultLight
        : theme.colors.backgroundColor.section};
    border: 1px solid ${theme.palette.borderGray};
    display: flex;
    align-items: center;
`;

const CtaWrapper = styled.div`
    ${ctaWrapperCss};
`;

const CtaWrapperLink = styled(BaseLink)`
    ${transition({}, "background-color")};
    ${ctaWrapperCss};

    &:hover {
        background-color: ${({ backgroundColor, theme }) =>
            backgroundColor === "white"
                ? theme.colors.backgroundColor.section
                : theme.colors.backgroundColor.defaultLight};

        &:hover .singleInnerCol {
            color: ${({ theme }) => theme.palette.teal};
        }
    }

    &:focus {
        background-color: ${({ backgroundColor, theme }) =>
            backgroundColor === "white"
                ? theme.colors.backgroundColor.section
                : theme.colors.backgroundColor.defaultLight};
        z-index: 20;
    }

    @media (forced-colors: active) {
        &:hover {
            &:hover .singleInnerCol {
                color: ButtonText;
            }
        }
    }
`;

const CtaWrapperButton = styled(Button)`
    ${ctaWrapperCss};
    border-radius: 0;
    text-align: left;

    &:hover {
        background-color: ${({ backgroundColor, theme }) =>
            backgroundColor === "white"
                ? theme.colors.backgroundColor.section
                : theme.colors.backgroundColor.defaultLight};

        &:hover .singleInnerCol {
            color: ${({ theme }) => theme.palette.teal};
        }
    }

    &:focus {
        background-color: ${({ backgroundColor, theme }) =>
            backgroundColor === "white"
                ? theme.colors.backgroundColor.section
                : theme.colors.backgroundColor.defaultLight};
        z-index: 20;
    }

    @media (forced-colors: active) {
        &:hover {
            &:hover .singleInnerCol {
                color: ActiveText;
            }
        }
    }
`;

const CtaWrapperButtonLink = CtaWrapperButton.withComponent(ButtonLink);

const InnerRow = styled.span`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: ${({ center }) => (center ? "center" : "flex-start")};
    margin: 0 -15px;
    flex: 1 1 auto;
`;

const InnerCol = styled.span`
    padding: 0 15px;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        width: ${({ inline }) => (inline ? `auto` : `100%`)};
        text-align: ${({ center }) => (center ? `center` : `left`)};
    }
`;

const SingleInnerCol = styled.span`
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 0 15px;
    width: 100%;
    margin-top: 0.5rem;
    font-size: ${({ center }) => (center ? "2.7rem" : "2rem")};

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        width: ${({ inline }) => (inline ? `auto` : `100%`)};
        justify-content: ${({ center }) => (center ? `center` : `left`)};
    }
`;

const CtaTitle = styled.span`
    ${h3};
    color: ${({ theme }) => theme.palette.black};
    font-weight: normal;
    margin-bottom: 0;
`;

const LinkText = styled.span`
    ${transition({}, "color")};
    font-weight: bold;
    color: ${({ theme }) => theme.palette.purple};
    line-height: 22px;
`;

const LinkIcon = styled(FaIcon)`
    ${transition({}, "color")};
    color: ${({ theme }) => theme.palette.purple};
    @media (forced-colors: active) {
        color: currentColor;
    }
`;

const CtaWithMultipleLinks = ({
    backgroundColor,
    center,
    inline,
    links,
    title,
    openModals,
    handleModalChange,
    trackLinkClick,
}) => {
    if (!title.length || links.length === 1) {
        center = true;
    }
    return (
        <CtaWrapper {...{ backgroundColor }}>
            <InnerRow {...{ center, inline }}>
                {title ? (
                    <InnerCol {...{ center, inline }}>
                        <CtaTitle>{title}</CtaTitle>
                    </InnerCol>
                ) : null}
                <InnerCol {...{ inline }}>
                    <InnerRow>
                        {links.map(
                            (
                                { linkLabel, linkUrl, icon, type, formId, doc },
                                index,
                            ) => (
                                <InnerCol key={index} {...{ center, inline }}>
                                    {type === "form" ? (
                                        <Form
                                            {...{ formId }}
                                            modalForm={true}
                                            isModalOpenProp={openModals.includes(
                                                formId,
                                            )}
                                            closeModalProp={() =>
                                                handleModalChange(formId)
                                            }
                                            useChildButton={true}
                                        >
                                            <IconButton
                                                {...{ icon }}
                                                onClick={() => {
                                                    handleModalChange(formId);
                                                    trackLinkClick(
                                                        linkLabel,
                                                        formId,
                                                        type,
                                                    );
                                                }}
                                                color="purple"
                                            >
                                                {linkLabel}
                                            </IconButton>
                                        </Form>
                                    ) : type === "document" ? (
                                        <GatedDocumentButton
                                            {...{ doc }}
                                            inline
                                            onClick={() =>
                                                trackLinkClick(
                                                    linkLabel,
                                                    doc.title,
                                                    type,
                                                )
                                            }
                                        >
                                            {icon ? (
                                                <Icon
                                                    name={icon}
                                                    iconLibrary="fas"
                                                />
                                            ) : null}
                                            {linkLabel}
                                        </GatedDocumentButton>
                                    ) : (
                                        <IconLink
                                            {...{ icon }}
                                            to={linkUrl}
                                            color="purple"
                                            onClick={() =>
                                                trackLinkClick(
                                                    linkLabel,
                                                    linkUrl,
                                                    type,
                                                )
                                            }
                                        >
                                            {linkLabel}
                                        </IconLink>
                                    )}
                                </InnerCol>
                            ),
                        )}
                    </InnerRow>
                </InnerCol>
            </InnerRow>
        </CtaWrapper>
    );
};

const SingleContent = ({ center, inline, title, linkLabel, icon }) => {
    return (
        <InnerRow {...{ center, inline }}>
            {title ? (
                <InnerCol {...{ center, inline }}>
                    <CtaTitle>{title}</CtaTitle>
                </InnerCol>
            ) : null}
            <InnerCol {...{ inline }}>
                <InnerRow>
                    <SingleInnerCol {...{ center, inline }}>
                        <LinkIcon className="singleInnerCol" name={icon} />
                        <LinkText className="singleInnerCol">
                            {linkLabel}
                        </LinkText>
                    </SingleInnerCol>
                </InnerRow>
            </InnerCol>
        </InnerRow>
    );
};

const CtaWithSingleLink = ({
    backgroundColor,
    center,
    inline,
    links,
    title,
    openModals,
    handleModalChange,
    trackLinkClick,
}) => {
    const { linkLabel, linkUrl, icon, type, formId, doc } = links[0];

    return type === "form" ? (
        <>
            <Form
                {...{ formId }}
                modalForm={true}
                isModalOpenProp={openModals.includes(formId)}
                closeModalProp={() => handleModalChange(formId)}
                useChildButton={true}
            />
            <CtaWrapperButton
                {...{ backgroundColor }}
                onClick={() => {
                    handleModalChange(formId);
                    trackLinkClick(linkLabel, formId, type);
                }}
            >
                <SingleContent
                    {...{ center, inline, title, linkLabel, icon }}
                />
            </CtaWrapperButton>
        </>
    ) : type === "document" ? (
        <GatedDocumentButton
            {...{ doc }}
            replacementComponent={CtaWrapperButtonLink}
            replacementComponentProps={{ backgroundColor }}
            inline
            onClick={() => trackLinkClick(linkLabel, doc.title, type)}
        >
            <SingleContent {...{ center, inline, title, linkLabel, icon }} />
        </GatedDocumentButton>
    ) : (
        <CtaWrapperLink
            {...{ backgroundColor, to: linkUrl }}
            onClick={() => trackLinkClick(linkLabel, linkUrl, type)}
        >
            <SingleContent {...{ center, inline, title, linkLabel, icon }} />
        </CtaWrapperLink>
    );
};

const Cta = ({
    links,
    backgroundColor = "gray",
    center = false,
    inline = false,
    title = null,
    ...props
}) => {
    const additionalProps = {
        backgroundColor,
        center,
        inline,
        title,
        ...props,
    };

    const [openModals, setOpenModals] = useState([]);

    const handleModalChange = (id) => {
        if (openModals.includes(id)) {
            setOpenModals(openModals.filter((modal) => modal !== id));
        } else {
            setOpenModals(openModals.concat(id));
        }
    };

    const trackLinkClick = (label, value, category) => {
        trackCustomEvent("gaEvent", {
            label,
            action: "CTA Section Link Click",
            value,
            category,
        });
    };

    return links?.length === 1 ? (
        <CtaWithSingleLink
            {...{
                ...additionalProps,
                openModals,
                links,
                handleModalChange,
                trackLinkClick,
            }}
        />
    ) : (
        <CtaWithMultipleLinks
            {...{
                ...additionalProps,
                openModals,
                links,
                handleModalChange,
                trackLinkClick,
            }}
        />
    );
};

Cta.propTypes = {
    backgroundColor: PropTypes.oneOf(["white", "gray"]),
    title: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string,
            icon: PropTypes.string,
            type: PropTypes.oneOf(["form", "link", "document"]),
            formId: PropTypes.number,
            doc: PropTypes.shape({}),
        }).isRequired,
    ).isRequired,
    center: PropTypes.bool,
    inline: PropTypes.bool,
};

export default Cta;
